import React, { useEffect, useId, useMemo, useState } from "react";
import {
  Box,
  ButtonBase,
  InputAdornment,
  List,
  ListItemButton,
  Popover,
  SxProps,
  TextField,
  TextFieldProps,
  Theme,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { Country, countriesService } from "../services/countriesService";
import { getDefaultInputLabelProps, getDefaultTextFieldSx } from "../theme/textFieldStyles";
import { BRAZIL_DDI, PhoneValue, onlyDigits, parsePhoneInput } from "../utils/phone";

/** País do seletor: só o que o campo precisa do catálogo. */
export type PhoneCountry = Pick<Country, "id" | "country" | "callingCode" | "iso2">;

const BRAZIL: PhoneCountry = { id: "", country: "Brasil", callingCode: BRAZIL_DDI, iso2: "BR" };

// DDI compartilhado por vários países (+1, +7, +44...): sem escolha explícita, mostra o país mais
// provável em vez do primeiro em ordem alfabética (Anguila para um número dos EUA).
const PREFERRED_ISO: Record<string, string> = {
  "1": "US", "7": "RU", "44": "GB", "47": "NO", "61": "AU", "39": "IT", "64": "NZ", "212": "MA", "357": "CY",
  "500": "FK", "590": "BL", "672": "NF", "970": "PS",
};

// E.164 permite até 15 dígitos no total.
const MAX_E164 = 15;
// Largura que o botão "+55 ▾" acrescenta ao campo DDD, que tem largura fixa definida pela tela
const DDD_ADORNMENT_WIDTH = 60;

const flagEmoji = (iso2?: string | null): string =>
  iso2 && /^[A-Za-z]{2}$/.test(iso2)
    ? String.fromCodePoint(...iso2.toUpperCase().split("").map((c) => 0x1f1e6 + c.charCodeAt(0) - 65))
    : "";

const widen = (
  width: number | Partial<Record<"xs" | "sm" | "md", number | string>>,
  extra: number
): number | Partial<Record<"xs" | "sm" | "md", number | string>> =>
  typeof width === "number"
    ? width + extra
    : Object.fromEntries(Object.entries(width).map(([bp, w]) => [bp, typeof w === "number" ? w + extra : w]));

const normalize = (text: string): string => text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

const matchesSearch = (c: PhoneCountry, query: string): boolean =>
  normalize(`${c.country} +${c.callingCode} ${c.iso2 ?? ""}`).includes(query);

/** Catálogo de países com DDI (uma leitura por sessão); Brasil sozinho enquanto carrega ou se falhar. */
const usePhoneCountries = (override?: PhoneCountry[]): PhoneCountry[] => {
  const [loaded, setLoaded] = useState<PhoneCountry[] | null>(null);

  useEffect(() => {
    if (override) return;
    let active = true;
    countriesService
      .phoneCountries()
      .then((list) => {
        if (active) setLoaded(list);
      })
      .catch(() => {
        if (active) setLoaded([]);
      });
    return () => {
      active = false;
    };
  }, [override]);

  const source = override ?? loaded ?? [];
  return source.length > 0 ? source : [BRAZIL];
};

interface PhoneWithCountryInputProps {
  /** Rótulo do campo do número (o mesmo que a tela já usava). */
  label: string;
  value: PhoneValue;
  onChange: (value: PhoneValue) => void;
  /**
   * DDD em campo próprio no Brasil (telas que já tinham DDD separado). Sem isso, DDD e número
   * ficam num campo só com a máscara (11) 99999-9999, como nas telas de paciente.
   */
  splitDdd?: boolean;
  dddLabel?: string;
  /** Largura do campo DDD, para casar com a tela (padrão 64px). */
  dddWidth?: number | Partial<Record<"xs" | "sm" | "md", number | string>>;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  placeholder?: string;
  /** Formulário anônimo (intake) passa a lista recebida da API; demais telas carregam /countries. */
  countries?: PhoneCountry[];
  /**
   * Campo mascarado (privacidade ou grupo "telefones" bloqueado no perfil): mostra um campo só,
   * com estas props, no lugar do seletor + número.
   */
  lockedProps?: Partial<TextFieldProps>;
  /** Tamanho/margem/estilo da tela; padrão = campos pequenos do design system. */
  size?: "small" | "medium";
  margin?: TextFieldProps["margin"];
  fieldSx?: TextFieldProps["sx"];
  labelProps?: TextFieldProps["InputLabelProps"];
  /** Espaço entre os campos (unidades do tema, pode variar por breakpoint), para casar com a linha da tela. */
  gap?: number | Partial<Record<"xs" | "sm" | "md", number>>;
  sx?: SxProps<Theme>;
  id?: string;
}

/**
 * Telefone com país: botão "+DDI ▾" dentro do próprio campo (no DDD, quando ele é separado),
 * que abre a lista de países com busca; Brasil por padrão. Máscara brasileira no +55, dígitos livres fora. Fora do Brasil
 * não há DDD. O valor sai em partes (DDI, DDD, número), como o backend grava.
 */
const PhoneWithCountryInput: React.FC<PhoneWithCountryInputProps> = ({
  label,
  value,
  onChange,
  splitDdd = false,
  dddLabel = "DDD",
  dddWidth = 64,
  required,
  disabled,
  error,
  helperText,
  placeholder,
  countries: override,
  lockedProps,
  size = "small",
  margin,
  fieldSx,
  labelProps,
  gap = 1,
  sx,
  id,
}) => {
  const countries = usePhoneCountries(override);
  const ddi = value.ddi || BRAZIL_DDI;
  const isBrazil = ddi === BRAZIL_DDI;
  // Escolha explícita entre países que dividem o DDI (ex.: Canadá em vez de EUA no +1).
  const [chosenIso, setChosenIso] = useState<string | null>(null);
  const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const listId = useId();

  const selected = useMemo(() => {
    const sameCode = countries.filter((c) => c.callingCode === ddi);
    return (
      sameCode.find((c) => c.iso2 === chosenIso) ??
      sameCode.find((c) => c.iso2 === (ddi === BRAZIL_DDI ? "BR" : PREFERRED_ISO[ddi])) ??
      sameCode[0] ??
      { id: `ddi-${ddi}`, country: `+${ddi}`, callingCode: ddi, iso2: null }
    );
  }, [countries, ddi, chosenIso]);

  // DDI gravado que o catálogo não tem (ou ainda não carregou) continua selecionável.
  const options = useMemo(() => (countries.includes(selected) ? countries : [selected, ...countries]), [countries, selected]);

  const filtered = useMemo(() => {
    const query = normalize(search.trim());
    return query ? options.filter((c) => matchesSearch(c, query)) : options;
  }, [options, search]);

  useEffect(() => {
    if (pickerAnchor) document.getElementById(`${listId}-${activeIndex}`)?.scrollIntoView({ block: "nearest" });
  }, [pickerAnchor, activeIndex, listId]);

  const common = {
    size,
    margin,
    disabled,
    sx: fieldSx ?? getDefaultTextFieldSx(),
    InputLabelProps: labelProps ?? getDefaultInputLabelProps(),
  };

  // Dado oculto ou restrito: um campo só, mascarado, sem o seletor de país
  if (lockedProps) {
    return (
      <Box sx={[{ width: "100%", minWidth: 0 }, ...(Array.isArray(sx) ? sx : [sx ?? {}])]}>
        <TextField fullWidth id={id} label={label} required={required} {...common} {...lockedProps} />
      </Box>
    );
  }

  const national = onlyDigits(value.ddd) + onlyDigits(value.number);
  const showDdd = splitDdd && isBrazil;
  const numberDigitsLimit = isBrazil ? (splitDdd ? 9 : 11) : MAX_E164 - ddi.length;

  const changeNumber = (raw: string) => {
    const digits = onlyDigits(raw);
    const current = splitDdd ? onlyDigits(value.number) : national;
    // Número colado inteiro ("+351 912...", "11 98765-4321" no campo sem DDD): separa DDI e DDD.
    // Só vale para colagem — digitar mais um dígito não pode virar DDD.
    const isPaste = raw.trim().startsWith("+")
      || (splitDdd && digits.length >= 10 && digits.length - current.length > 1);
    const pasted = isPaste ? parsePhoneInput(raw) : null;
    if (pasted) {
      setChosenIso(null);
      onChange(splitDdd || pasted.ddi !== BRAZIL_DDI ? pasted : { ...pasted, ddd: "", number: pasted.ddd + pasted.number });
      return;
    }
    const number = digits.slice(0, numberDigitsLimit);
    onChange(splitDdd ? { ...value, ddi, number } : { ddi, ddd: "", number });
  };

  // Só dígitos no campo, para qualquer país; a máscara fica nas telas de exibição (formatPhoneDisplay)
  const displayNumber = splitDdd ? onlyDigits(value.number) : national;

  const openPicker = (anchor: HTMLElement | null) => {
    setSearch("");
    setActiveIndex(Math.max(0, options.indexOf(selected)));
    setPickerAnchor(anchor);
  };

  const closePicker = () => {
    setPickerAnchor(null);
    setSearch("");
  };

  const selectCountry = (c: PhoneCountry) => {
    closePicker();
    if (!c.callingCode) return;
    setChosenIso(c.iso2);
    // Fora do Brasil não há DDD: o que estava nele vai para o número.
    onChange(
      c.callingCode === BRAZIL_DDI && splitDdd
        ? { ...value, ddi: c.callingCode }
        : { ddi: c.callingCode, ddd: "", number: national }
    );
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[activeIndex]) selectCountry(filtered[activeIndex]);
    }
  };

  // O DDI fica no primeiro campo da linha: o DDD, quando é separado, senão o próprio número
  const countryAdornment = (
    <InputAdornment position="start" sx={{ mr: 0.25 }}>
      <ButtonBase
        disabled={disabled}
        onClick={(e) => openPicker(e.currentTarget.closest<HTMLElement>(".MuiInputBase-root"))}
        aria-haspopup="listbox"
        aria-label={`País do ${label}: ${selected.country} +${ddi}`}
        sx={{
          font: "inherit",
          color: "text.primary",
          whiteSpace: "nowrap",
          borderRadius: 1,
          pl: 0.5,
          ml: -0.5,
          "&:hover, &.Mui-focusVisible": { bgcolor: "action.hover" },
        }}
      >
        +{ddi}
        <ArrowDropDownIcon fontSize="small" sx={{ color: "text.secondary", mr: -0.5 }} />
      </ButtonBase>
    </InputAdornment>
  );

  return (
    <Box sx={[{ width: "100%", minWidth: 0 }, ...(Array.isArray(sx) ? sx : [sx ?? {}])]}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap, width: "100%", minWidth: 0 }}>
        {showDdd && (
          <Box sx={{ width: widen(dddWidth, DDD_ADORNMENT_WIDTH), flexShrink: 0 }}>
            <TextField
              {...common}
              fullWidth
              id={id ? `${id}-ddd` : undefined}
              label={dddLabel}
              value={value.ddd}
              onChange={(e) => onChange({ ...value, ddi, ddd: onlyDigits(e.target.value).slice(0, 2) })}
              inputProps={{ inputMode: "numeric", maxLength: 2 }}
              InputProps={{ startAdornment: countryAdornment }}
              error={error}
            />
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <TextField
            {...common}
            fullWidth
            id={id}
            label={label}
            required={required}
            value={displayNumber}
            onChange={(e) => changeNumber(e.target.value)}
            placeholder={isBrazil ? placeholder : undefined}
            inputProps={{ inputMode: "tel" }}
            InputProps={showDdd ? undefined : { startAdornment: countryAdornment }}
            error={error}
          />
        </Box>
      </Box>
      <Popover
        open={!!pickerAnchor}
        anchorEl={pickerAnchor}
        onClose={closePicker}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: { width: 300, maxWidth: "calc(100vw - 32px)", mt: 0.5, display: "flex", flexDirection: "column" },
          },
        }}
      >
        <Box sx={{ p: 1, flexShrink: 0 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Buscar país ou código"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setActiveIndex(0);
            }}
            onKeyDown={handleSearchKeyDown}
            inputProps={{
              role: "combobox",
              "aria-expanded": true,
              "aria-controls": listId,
              "aria-activedescendant": filtered[activeIndex] ? `${listId}-${activeIndex}` : undefined,
            }}
          />
        </Box>
        <List id={listId} role="listbox" dense sx={{ py: 0, maxHeight: 320, minHeight: 0, overflowY: "auto" }}>
          {filtered.map((c, i) => (
            <ListItemButton
              key={`${c.id}-${c.iso2}-${c.callingCode}`}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={c === selected}
              selected={i === activeIndex}
              onMouseMove={() => setActiveIndex(i)}
              onClick={() => selectCountry(c)}
              sx={{ fontWeight: c === selected ? 600 : undefined }}
            >
              <Box component="span" sx={{ mr: 1 }}>{flagEmoji(c.iso2)}</Box>
              <Box component="span" sx={{ flex: 1, minWidth: 0 }}>{c.country}</Box>
              <Box component="span" sx={{ ml: 1, color: "text.secondary" }}>+{c.callingCode}</Box>
            </ListItemButton>
          ))}
          {filtered.length === 0 && (
            <Box sx={{ px: 2, py: 1.5, color: "text.secondary", fontSize: "0.875rem" }}>Nenhum país encontrado</Box>
          )}
        </List>
      </Popover>
      {helperText && (
        <Box sx={{ mt: 0.5, mx: 1.75, fontSize: "0.75rem", color: error ? "error.main" : "text.secondary" }}>
          {helperText}
        </Box>
      )}
    </Box>
  );
};

export default PhoneWithCountryInput;
